import React, { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Templatestyle,
  Templateiconstyle,
  Templatetextstyle,
  TemplateSelectedLanguageRadioButton,
} from "../../atoms/ConnectStories";
import SelectLanguage from "../../../images/select_language.svg";
import { useTranslation } from "react-i18next";
import useApiRepository from "../../../api/useApiRepository";

export const LanguageSelection = ({ onLanguageChange }) => {
const { t } = useTranslation();
  const apiRepo = useApiRepository();
  const { loginReducer, 
    persistItemsReducer, 
    writingTemplatesReducer } = useSelector((state) => state);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const localStorageKey = `selectedLang-${persistItemsReducer?.selectedSOData?.soCode}-${persistItemsReducer?.sponsorID}`
  const savedLang = localStorage.getItem(localStorageKey);
  // Memoized supported languages list
  const supportedLanguages = loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.supportedLanguages || [];

  // Check if language selection should be rendered
  const shouldShowLanguageSelection =
    loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.multipleLanguagesSupported ||
    supportedLanguages.length > 0;

  // Memoized language code getter with optimized dependencies
  const getLanguageCode = useCallback(
    (name, reverse = false) => {
      const match = supportedLanguages.find((item) =>
        reverse ? item.languageCode === name : item.languageName === name,
      );
      return reverse ? match?.languageName || "" : match?.languageCode || "en";
    },
    [supportedLanguages],
  );

  // Memoized handler for fetching templates and notifying parent (single source of truth)
  // Deferred to prevent flicker on iPhone during rapid state updates
  const fetchTemplatesAndNotify = useCallback(
    (languageName) => {
      const languageCode = getLanguageCode(languageName);
      // Defer API call and notification to next tick to allow UI batching
      setTimeout(() => {
        if (!writingTemplatesReducer.loading) {
          apiRepo.connectRepository.getWritingTemplates(languageCode, true);
          onLanguageChange?.({ languageName, languageCode });
        }
      }, 0);
    },
    [getLanguageCode, apiRepo, onLanguageChange]
  );

  // Initialize language on mount only
  useEffect(() => {
    let connectScreenLanguage = "English"
    if (savedLang ) {
      connectScreenLanguage = savedLang;
    } else if (persistItemsReducer?.selectedSOData?.soCode === "JPNO") { 
        connectScreenLanguage ="Japanese";
    }
      setSelectedLanguage(connectScreenLanguage);
      fetchTemplatesAndNotify(connectScreenLanguage);
  }, []); // Empty dependency - mount only

  // Memoized language change handler - prevent inline function recreation
  // Batches state update to prevent iPhone flickering
  const handleLanguageChange = useCallback(
    (languageName) => {
      // All updates in same callback batch together (React 18 auto-batching)
      // This prevents multiple re-renders on iPhone
      setSelectedLanguage(languageName);
      localStorage.setItem(localStorageKey, languageName);
      // Defer async API call to prevent rendering conflict
      fetchTemplatesAndNotify(languageName);
    },
    [fetchTemplatesAndNotify, localStorageKey]
  );

  // Monitor occasions and re-fetch if undefined (handles tab duplication and missing templates)
  useEffect(() => {
      if ( !writingTemplatesReducer.loading && 
        !writingTemplatesReducer?.getWritingTemplates?.occasions && 
        selectedLanguage) {
      const languageCode = getLanguageCode(selectedLanguage);
      apiRepo.connectRepository.getWritingTemplates(languageCode, true);
    }
  }, [writingTemplatesReducer?.getWritingTemplates?.occasions, selectedLanguage, getLanguageCode, apiRepo]);

  return shouldShowLanguageSelection ? (
    <Templatestyle style={{ width: "100%" }}>
      <Templateiconstyle>
        <img src={SelectLanguage} alt="" />
      </Templateiconstyle>
      <Templatetextstyle style={{ paddingRight: "20px" }}>
        {t("SelectLanguage")}
      </Templatetextstyle>

      <TemplateSelectedLanguageRadioButton>
        {loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.supportedLanguages?.map(
          (ele) => {
            const soCode = persistItemsReducer?.selectedSOData?.soCode;
            let displayLanguage;

            if (soCode === "AUTO") {
              displayLanguage =
                ele.languageName === "German" ? "Deutsch" : "Englisch";
            } else if (soCode === "JPNO") {
              displayLanguage =
                ele.languageName === "Japanese" ? t("Japanese") : "English";
            } else {
              displayLanguage =
                ele.languageName === "Finnish" ? "suomi" : "englanti";
            }

            return (
              <span
                key={ele.languageName}
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  marginLeft: "15px",
                }}
              >
                <input
                  type="radio"
                  name="language"
                  value={ele.languageName}
                  checked={ele.languageName === selectedLanguage && savedLang}
                  onChange={() => handleLanguageChange(ele.languageName)}
                />
                <span>{displayLanguage}</span>
              </span>
            );
          },
        )}
      </TemplateSelectedLanguageRadioButton>
    </Templatestyle>
  ) : null;
};
